import React, { useEffect, useRef, useState } from "react";
import PrescriptionServices from "../../Services/PrescriptionServices";
import dayjs from "dayjs";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import FooterPrescription from "./FooterPrescription";
import HeaderPrescription from "./HeaderPrescription";
import { Helmet } from "react-helmet";
import { Flex, message, Spin } from "antd";

export default function SharePrescription() {
  const contentRef = useRef(null);
  const wrapperRef = useRef(null);
  const paperRef = useRef(null);

  const [prescriptionData, setPrescriptionData] = useState(null);
  const [accountDetails, setAccountDetails] = useState(null);
  const [clinicDetails, setClinicDetails] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [patientDetails, setPatientDetails] = useState(null);
  const [consultationDetails, setConsultationDetails] = useState(null);
  const [doctorSign, setDoctorSign] = useState(null);
  const [doctorStamp, setDoctorStamp] = useState(null);
  const [specialityNotes, setSpecialityNotes] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isPDFReady, setIsPDFReady] = useState(false);

  useEffect(() => {
    if (isPDFReady) {
      download();
    }
  }, [isPDFReady]);

  const A4_W_MM = 210;
  const A4_H_MM = 297;
  const PX_PER_MM = 96 / 25.4;
  const A4_W_PX = Math.round(A4_W_MM * PX_PER_MM);
  const A4_H_PX = Math.round(A4_H_MM * PX_PER_MM);

  const HORIZONTAL_PADDING = 12;
  const VERTICAL_PADDING = 12;
  const MAX_SCALE = 1.6;
  const MIN_SCALE = 0.28;

  const unitMap = {
    BP: "mmHg",
    BPS: "mmHg",
    BPD: "mmHg",
    HR: "Beats/min",
    RR: "Breaths/min",
    TP: "°F",
    HI: "cm",
    WE: "kg",
    BMI: "Kg/m²",
    SPO2: "%",
  };

  const displayOrder = ["BP", "HR", "RR", "TP", "SPO2", "HI", "WE", "BMI"];

  const getCodeFromURL = () => {
    const query = window.location.search;
    const match = query.match(/ref=([^&/]*)/);
    return match ? match[1] : null;
  };

  useEffect(() => {
    const param = getCodeFromURL();
    if (!param) {
      setLoading(false);
      return;
    }
    setLoading(true);

    PrescriptionServices.getPrescriptionFromBEPDF(param)
      .then((res) => {
        if (res?.data?.prescriptionName) {
          window.location.replace(
            process.env.REACT_APP_IMAGE_URL + res.data.prescriptionName
          );
        } else {
          message.error("Failed to load prescription");
        }
      })
      .catch((error) => {
        message.error("Failed to load prescription");
      });

    return;
    PrescriptionServices.getSharePrescription(param)
      .then((res) => {
        const data = res.data;
        setPrescriptionData(data);
        setClinicDetails(data?.clinicDetails);
        setUserDetails(data?.userDetails);
        if (data?.userDetails) {
          const u = data.userDetails;
          if (u.doctorSignatureB64)
            setDoctorSign("data:image/jpeg;base64," + u.doctorSignatureB64);
          if (u.doctorStampB64)
            setDoctorStamp("data:image/jpeg;base64," + u.doctorStampB64);
        }
        setAccountDetails(data?.accountDetails);
        setPatientDetails(data?.patientDetails);
        setConsultationDetails(data?.consultationDetails);
        setSpecialityNotes(data?.specialityDetails);

        setIsPDFReady(true);
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        message.error("Error occurred");
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev || "";
    };
  }, []);

  // helper: wait for images in an element
  const waitForImages = (rootEl, timeout = 5000) =>
    Promise.all(
      Array.from(rootEl.querySelectorAll("img")).map((img) => {
        return new Promise((resolve) => {
          if (!img.src) return resolve();
          if (img.complete && img.naturalWidth !== 0) return resolve();
          let done = false;
          const onDone = () => {
            if (done) return;
            done = true;
            img.removeEventListener("load", onDone);
            img.removeEventListener("error", onDone);
            resolve();
          };
          img.addEventListener("load", onDone);
          img.addEventListener("error", onDone);
          setTimeout(() => {
            if (!done) {
              done = true;
              resolve();
            }
          }, timeout);
        });
      })
    );

  useEffect(() => {
    const applyScale = async () => {
      if (!paperRef.current || !wrapperRef.current || !contentRef.current)
        return;

      // ensure images/fonts loaded so measurements are stable
      try {
        if (document.fonts && document.fonts.ready) await document.fonts.ready;
      } catch (e) {}
      try {
        await waitForImages(paperRef.current, 1000);
      } catch (e) {}

      const vw = Math.max(window.innerWidth - HORIZONTAL_PADDING * 2, 120);
      const vh = Math.max(window.innerHeight - VERTICAL_PADDING * 2, 120);

      let rawScale = Math.min(vw / A4_W_PX, vh / A4_H_PX);
      const clamped = Math.max(MIN_SCALE, Math.min(rawScale, MAX_SCALE));
      const supportsZoom = "zoom" in document.body.style;

      // Keep paper a natural flowing element (minHeight ensures page baseline), avoid clipping on preview
      paperRef.current.style.width = `${A4_W_PX}px`;
      paperRef.current.style.minHeight = `${A4_H_PX}px`;
      paperRef.current.style.height = "auto";
      paperRef.current.style.boxSizing = "border-box";
      paperRef.current.style.padding = "0";
      paperRef.current.style.position = "relative";
      paperRef.current.style.overflow = "visible"; // allow content to flow and not be clipped in preview
      paperRef.current.style.fontSize = "11px";

      if (supportsZoom) {
        paperRef.current.style.zoom = `${clamped}`;
        paperRef.current.style.transform = "none";
      } else {
        paperRef.current.style.transformOrigin = "top left";
        paperRef.current.style.transform = `scale(${clamped})`;
        paperRef.current.style.zoom = "";
      }

      const scaledW = Math.round(A4_W_PX * clamped);
      // Allow wrapper to grow in height so preview is accurate; only set width (for centering)
      wrapperRef.current.style.width = `${scaledW}px`;
      wrapperRef.current.style.height = "auto";

      // viewer scroll stays in outer contentRef
      contentRef.current.style.width = "100vw";
      contentRef.current.style.height = "100vh";
      contentRef.current.style.padding = "0";
      contentRef.current.style.boxSizing = "border-box";
      contentRef.current.style.overflowY = "auto";
      contentRef.current.style.overflowX = "hidden";

      wrapperRef.current.style.margin = "0 auto";
      wrapperRef.current.style.display = "flex";
      wrapperRef.current.style.justifyContent = "center";
      wrapperRef.current.style.alignItems = "flex-start";
    };

    if (isPDFReady) {
      applyScale();
      window.addEventListener("resize", applyScale);
      window.addEventListener("orientationchange", applyScale);
    }
    return () => {
      window.removeEventListener("resize", applyScale);
      window.removeEventListener("orientationchange", applyScale);
    };
  }, [isPDFReady]);

  const download = async () => {
    const originalPaper = paperRef.current;
    if (!originalPaper) return;

    const temp = originalPaper.cloneNode(true);
    temp.style.width = `${A4_W_PX}px`;
    temp.style.height = "auto";
    temp.style.transform = "none";
    temp.style.zoom = "";
    temp.style.position = "absolute";
    temp.style.left = "-99999px";
    temp.style.top = "0";
    temp.style.background = "#fff";
    document.body.appendChild(temp);

    try {
      if (document.fonts && document.fonts.ready) await document.fonts.ready;
      await waitForImages(temp, 5000);
      await new Promise((r) => setTimeout(r, 120));
    } catch (e) {}

    // mark img cors
    Array.from(temp.querySelectorAll("img")).forEach((img) => {
      try {
        img.setAttribute("crossorigin", "anonymous");
      } catch (e) {}
    });

    try {
      const canvas = await html2canvas(temp, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
      });

      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "px",
        format: "a4",
      });

      const pdfW = pdf.internal.pageSize.getWidth();
      const pdfH = pdf.internal.pageSize.getHeight();

      const pageHeightPx = A4_H_PX * 2; // because scale=2
      const totalHeight = canvas.height;
      const totalPages = Math.ceil(totalHeight / pageHeightPx);

      for (let page = 0; page < totalPages; page++) {
        const pageCanvas = document.createElement("canvas");
        pageCanvas.width = canvas.width;
        const remaining = totalHeight - page * pageHeightPx;
        pageCanvas.height = remaining > pageHeightPx ? pageHeightPx : remaining;

        const ctx = pageCanvas.getContext("2d");
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
        ctx.drawImage(
          canvas,
          0,
          page * pageHeightPx,
          canvas.width,
          pageCanvas.height,
          0,
          0,
          pageCanvas.width,
          pageCanvas.height
        );

        const imgData = pageCanvas.toDataURL("image/png");
        const imgWidth = pdfW;
        const imgHeight = (pageCanvas.height * imgWidth) / pageCanvas.width;
        if (page > 0) pdf.addPage();
        pdf.addImage(imgData, "PNG", 0, 0, imgWidth, imgHeight);
      }

      // 👉 Convert PDF to Blob + URL
      const blob = pdf.output("blob");
      const url = URL.createObjectURL(blob);
      // 👉 Open PDF in the SAME tab
      window.location.assign(url);
      // OPTIONAL auto-download:
      const a = document.createElement("a");
      a.href = url;
      a.download = "prescription.pdf";
      a.click();
    } catch (err) {
      console.error("PDF capture error:", err);
      message.error("Failed to create PDF");
    } finally {
      if (temp && temp.parentNode) document.body.removeChild(temp);
    }
  };

  const computeAge = (dob) => {
    if (!dob) return null;
    const parts = dob.includes("/") ? dob.split("/") : dob.split("-");
    const year = parts.length === 3 ? parts[2] : parts[0];
    if (year && !isNaN(Number(year)))
      return new Date().getFullYear() - Number(year);
    const parsed = new Date(dob);
    return isNaN(parsed.getTime())
      ? null
      : new Date().getFullYear() - parsed.getFullYear();
  };

  const rawVitalsList = (
    consultationDetails?.vitalsPrescriptionDetails || []
  ).filter(
    (v) =>
      v &&
      v.vitalCode &&
      v.value !== undefined &&
      v.value !== null &&
      `${v.value}`.toString().trim() !== ""
  );

  const bpsItem = rawVitalsList.find((v) => v.vitalCode === "BPS");
  const bpdItem = rawVitalsList.find((v) => v.vitalCode === "BPD");
  const processedVitals = [];
  if (bpsItem && bpdItem) {
    processedVitals.push({
      vitalCode: "BP",
      value: `${bpsItem.value}/${bpdItem.value}`,
      unit: unitMap.BP,
    });
  } else if (bpsItem) {
    processedVitals.push({
      vitalCode: "BP",
      value: `${bpsItem.value}`,
      unit: unitMap.BP,
    });
  } else if (bpdItem) {
    processedVitals.push({
      vitalCode: "BP",
      value: `${bpdItem.value}`,
      unit: unitMap.BP,
    });
  }
  rawVitalsList.forEach((v) => {
    if (v.vitalCode === "BPS" || v.vitalCode === "BPD") return;
    processedVitals.push({
      vitalCode: v.vitalCode,
      value: v.value,
      unit: v.unit || unitMap[v.vitalCode] || "",
    });
  });

  const vitalsMap = {};
  processedVitals.forEach((v) => {
    vitalsMap[v.vitalCode] = {
      value: v.value,
      unit: v.unit || unitMap[v.vitalCode] || "",
    };
  });

  const vitalsCellStyle = {
    minWidth: 100,
    paddingRight: 8,
    fontSize: 11,
    color: "#374151",
  };

  const formattedDate = (() => {
    const d = prescriptionData?.consultationDate?.slice(0, 11).split("-");
    return d?.length === 3 ? `${d[2].trim()}-${d[1]}-${d[0]}` : "";
  })();

  const patientAgeVal = patientDetails?.patientDob
    ? computeAge(patientDetails?.patientDob)
    : "";

  const patientInfo = {
    patientName:
      patientDetails?.patientName ||
      `${patientDetails?.patientFirstName ?? ""} ${patientDetails?.patientLastName ?? ""}`.trim(),
    age: patientAgeVal
      ? `${patientAgeVal}${patientDetails?.patientGender ? ` / ${patientDetails.patientGender}` : ""}`
      : "",
    date:
      formattedDate ||
      (prescriptionData?.consultationDate
        ? dayjs(prescriptionData.consultationDate).format("DD-MM-YYYY")
        : ""),
    patientId: patientDetails?.patientId || prescriptionData?.patientId,
    mobile: patientDetails?.patientMobile,
  };

  return (
    <>
      <Helmet>
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <style>{`body{margin:0;padding:0;background:#f3f4f6} p{margin:0}`}</style>
      </Helmet>

      {loading ? (
        <div
          style={{
            position: "fixed",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "#fff",
            zIndex: 50,
          }}
        >
          <Flex align="center" gap="middle">
            <Spin size="large" tip="Loading" />
          </Flex>
        </div>
      ) : (
        <div
          ref={contentRef}
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "flex-start",
            minHeight: "100vh",
          }}
        >
          <div ref={wrapperRef}>
            <div
              ref={paperRef}
              style={{
                width: `${A4_W_PX}px`,
                minHeight: `${A4_H_PX}px`,
                background: "#fff",
                boxSizing: "border-box",
                padding: 0,
                overflow: "visible",
              }}
            >
              <div
                className="pageInner"
                style={{
                  padding: `${VERTICAL_PADDING}px ${HORIZONTAL_PADDING}px`,
                  boxSizing: "border-box",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <div style={{ flex: "0 0 auto" }}>
                  <HeaderPrescription
                    clinicDetails={clinicDetails}
                    doctorData={userDetails}
                    hospitalAccountData={accountDetails}
                    patientInfo={patientInfo}
                  />
                </div>

                <div style={{ flex: "1 1 auto", overflow: "visible" }}>
                  {userDetails?.isPatientInfoRequired === "Y" && (
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "flex-end",
                        gap: 8,
                        marginTop: 12,
                        fontWeight: 600,
                        fontSize: 11,
                      }}
                    >
                      <span>Date:</span>
                      <span style={{ fontWeight: 500 }}>
                        {dayjs(prescriptionData?.consultationDate).format(
                          "DD-MM-YYYY"
                        )}
                      </span>
                    </div>
                  )}

                  <div style={{ marginTop: 10, fontSize: 11 }}>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
                      {userDetails?.isPatientInfoRequired === "Y" && (
                        <div style={{ fontWeight: 600 }}>
                          Name:
                          <span
                            style={{
                              fontWeight: 400,
                              color: "#374151",
                              marginLeft: 6,
                            }}
                          >
                            {patientDetails?.patientSalutation}{" "}
                            {patientDetails?.patientFirstName}{" "}
                            {patientDetails?.patientLastName}
                          </span>
                        </div>
                      )}

                      {userDetails?.isPatientInfoRequired === "Y" && (
                        <div style={{ fontWeight: 600 }}>
                          Age/Gender:
                          <span
                            style={{
                              fontWeight: 400,
                              color: "#374151",
                              marginLeft: 6,
                            }}
                          >
                            {patientDetails?.patientDob
                              ? computeAge(patientDetails?.patientDob)
                              : "NA"}{" "}
                            / {patientDetails?.patientGender || "NA"}
                          </span>
                        </div>
                      )}

                      <div style={{ fontWeight: 600 }}>
                        Phone:
                        <span
                          style={{
                            fontWeight: 400,
                            color: "#374151",
                            marginLeft: 6,
                          }}
                        >
                          {patientDetails?.patientMobile || "NA"}
                        </span>
                      </div>

                      <div style={{ fontWeight: 600 }}>
                        Visit:
                        <span
                          style={{
                            fontWeight: 400,
                            color: "#374151",
                            marginLeft: 6,
                          }}
                        >
                          {consultationDetails?.consultationType || "NA"}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div
                    style={{ borderBottom: "1px solid #e5e7eb", marginTop: 12 }}
                  />

                  <div style={{ marginTop: 8 }}>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(5, 1fr)",
                        gap: 12,
                      }}
                    >
                      {Object.entries(vitalsMap)
                        .filter(([code, v]) => v?.value && v.value !== "-") // show only those with values
                        .map(([code, v]) => (
                          <div
                            key={code}
                            style={{ ...vitalsCellStyle, minHeight: 36 }}
                          >
                            <span style={{ fontWeight: 700, fontSize: 11 }}>
                              {code}:
                            </span>
                            <span style={{ fontSize: 11 }}>
                              {v.value} {v.unit || ""}
                            </span>
                          </div>
                        ))}
                    </div>
                  </div>

                  <div
                    style={{ borderBottom: "1px solid #e5e7eb", marginTop: 12 }}
                  />

                  <div style={{ marginTop: 12 }}>
                    <div style={{ fontWeight: 700, fontSize: 12 }}>
                      Chief Complaint:
                    </div>
                    <div
                      style={{ marginTop: 6, fontSize: 11, color: "#374151" }}
                    >
                      {consultationDetails?.consultationReason || "-"}
                    </div>

                    <div
                      style={{ marginTop: 10, fontWeight: 700, fontSize: 12 }}
                    >
                      Symptoms:
                    </div>
                    <div
                      style={{ marginTop: 6, fontSize: 11, color: "#374151" }}
                    >
                      {consultationDetails?.symptoms || "-"}
                    </div>

                    {consultationDetails?.diagnosisPrescriptionDetails?.some(
                      (d) => d.diagnosisType === "Primary"
                    ) && (
                      <>
                        <div
                          style={{
                            marginTop: 10,
                            fontWeight: 700,
                            fontSize: 12,
                          }}
                        >
                          Primary Diagnosis:
                        </div>

                        <div
                          style={{
                            marginTop: 6,
                            fontSize: 11,
                            color: "#374151",
                            whiteSpace: "pre-line",
                          }}
                        >
                          {consultationDetails.diagnosisPrescriptionDetails
                            .filter((d) => d.diagnosisType === "Primary")
                            .map(
                              (d, idx) =>
                                `${idx + 1}. ${d.diagnosisDescription}`
                            )
                            .join("\n")}
                        </div>
                      </>
                    )}

                    {consultationDetails?.diagnosisPrescriptionDetails?.some(
                      (d) => d.diagnosisType === "Secondary"
                    ) && (
                      <>
                        <div
                          style={{
                            marginTop: 10,
                            fontWeight: 700,
                            fontSize: 12,
                          }}
                        >
                          Secondary Diagnosis:
                        </div>

                        <div
                          style={{
                            marginTop: 6,
                            fontSize: 11,
                            color: "#374151",
                            whiteSpace: "pre-line",
                          }}
                        >
                          {consultationDetails.diagnosisPrescriptionDetails
                            .filter((d) => d.diagnosisType === "Secondary")
                            .map(
                              (d, idx) =>
                                `${idx + 1}. ${d.diagnosisDescription}`
                            )
                            .join("\n")}
                        </div>
                      </>
                    )}
                  </div>
                  {specialityNotes?.some(
                    (s) => s?.componentDetailsList?.length > 0
                  ) && (
                    <div className="mt-2 mb-5">
                      <p className="mb-2">
                        <b className="font-bold">Clinical Notes :</b>
                      </p>

                      <div className="md:w-[60%] w-full">
                        {specialityNotes.map((template, idx) => {
                          if (!template?.componentDetailsList?.length)
                            return null;

                          return (
                            <div key={idx} className="mb-3">
                              {/* Template title */}
                              {/* <p>
                                <b className="font-medium border p-1 rounded-lg bg-brand-lightBg">
                                  {template.templateName}
                                </b>
                              </p> */}

                              {/* Fields list */}
                              <div className="w-full flex items-center gap-2 flex-wrap mt-1">
                                {template.componentDetailsList.map(
                                  (field, index) => (
                                    <div
                                      key={index}
                                      className="text-sm flex items-center gap-1"
                                    >
                                      <p className="font-medium w-max my-1 py-1">
                                        {field.componentLabel}
                                      </p>

                                      <span>:</span>

                                      <p className="text-gray-700 w-max my-1 py-1">
                                        {field.componentValue}
                                      </p>

                                      {/* Separator except last */}
                                      {index !==
                                        template.componentDetailsList.length -
                                          1 && (
                                        <span className="font-medium">|</span>
                                      )}
                                    </div>
                                  )
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  <div
                    style={{ borderBottom: "1px solid #e5e7eb", marginTop: 12 }}
                  />

                  {consultationDetails?.drugPrescriptionDetails && (
                    <div style={{ marginTop: 12 }}>
                      <div style={{ fontWeight: 700, fontSize: 12 }}>
                        Medicines:
                      </div>
                      <div
                        style={{
                          overflowX: "auto",
                          WebkitOverflowScrolling: "touch",
                          marginTop: 8,
                        }}
                      >
                        <table
                          style={{
                            width: "100%",
                            borderCollapse: "collapse",
                            minWidth: 640,
                            fontSize: 11,
                          }}
                        >
                          <thead>
                            <tr style={{ borderBottom: "2px solid #e5e7eb" }}>
                              <th style={{ textAlign: "left", padding: 8 }}>
                                Medication
                              </th>
                              <th style={{ textAlign: "left", padding: 8 }}>
                                Dosage
                              </th>
                              <th style={{ textAlign: "left", padding: 8 }}>
                                Duration
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {consultationDetails.drugPrescriptionDetails.map(
                              (medicine, idx) => (
                                <tr
                                  key={idx}
                                  style={{ borderBottom: "1px solid #e5e7eb" }}
                                >
                                  <td
                                    style={{
                                      padding: 8,
                                      verticalAlign: "top",
                                      wordBreak: "break-word",
                                    }}
                                  >
                                    {medicine.drugName}
                                  </td>
                                  <td
                                    style={{
                                      padding: 8,
                                      verticalAlign: "top",
                                      wordBreak: "break-word",
                                    }}
                                  >
                                    <div>{medicine.dosage}</div>
                                    <div
                                      style={{
                                        marginTop: 6,
                                        fontSize: 10,
                                        color: "#374151",
                                      }}
                                    >
                                      {medicine?.afterBeforeFood === "AF"
                                        ? "After Food"
                                        : medicine?.afterBeforeFood === "BF"
                                        ? "Before Food"
                                        : ""}
                                    </div>
                                  </td>
                                  <td
                                    style={{
                                      padding: 8,
                                      verticalAlign: "top",
                                      wordBreak: "break-word",
                                    }}
                                  >
                                    <div style={{ fontSize: 11 }}>
                                      {medicine.duration} days
                                    </div>
                                    <div
                                      style={{
                                        marginTop: 6,
                                        fontSize: 10,
                                        color: "#374151",
                                      }}
                                    >
                                      {medicine?.instruction}
                                    </div>
                                  </td>
                                </tr>
                              )
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {consultationDetails?.labPrescriptionDetails && (
                    <div style={{ marginTop: 12 }}>
                      <div style={{ fontWeight: 700, fontSize: 11 }}>
                        Lab Test:
                      </div>
                      <div style={{ marginTop: 8 }}>
                        <div
                          style={{
                            fontWeight: 700,
                            border: "1px solid #e5e7eb",
                            padding: 6,
                            fontSize: 11,
                          }}
                        ></div>
                        {consultationDetails?.labPrescriptionDetails &&
                          consultationDetails.labPrescriptionDetails.length >
                            0 &&
                          consultationDetails.labPrescriptionDetails.map(
                            (lab, idx) => (
                              <div
                                key={idx}
                                style={{
                                  border: "1px solid #e5e7eb",
                                  padding: 6,
                                  marginTop: 6,
                                  fontSize: 11,
                                }}
                              >
                                {lab.labTestName}
                              </div>
                            )
                          )}
                      </div>
                    </div>
                  )}

                  {consultationDetails?.recommendation && (
                    <div
                      className="recommendation-block"
                      style={{ marginTop: 18 }}
                    >
                      <div style={{ marginBottom: 8 }}>
                        <p style={{ fontSize: 11 }}>
                          <strong>Recommendation:</strong>{" "}
                          {consultationDetails?.recommendation || "-"}
                        </p>
                      </div>
                    </div>
                  )}
                  {consultationDetails?.followupVisitDate && (
                    <div
                      className="recommendation-block"
                      style={{ marginTop: 18 }}
                    >
                      <div style={{ marginBottom: 8 }}>
                        <p style={{ fontSize: 11 }}>
                          <strong>Scheduled Follow-up:</strong>{" "}
                          {consultationDetails?.followupVisitDate || "-"}
                        </p>
                      </div>
                    </div>
                  )}

                  <div className="signature-block" style={{ marginTop: 8 }}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-end",
                        gap: 12,
                      }}
                    >
                      <div style={{ flex: "0 0 140px", textAlign: "center" }}>
                        {doctorStamp && (
                          <img
                            className="stamp-image"
                            src={doctorStamp}
                            alt="Doctor Stamp"
                            style={{
                              display: "block",
                              maxWidth: 120,
                              height: "auto",
                              margin: "0 auto",
                              objectFit: "contain",
                              background: "#ffffff",
                            }}
                            onError={(e) =>
                              (e.currentTarget.style.display = "")
                            }
                          />
                        )}
                      </div>

                      <div style={{ flex: "0 0 220px", textAlign: "center" }}>
                        {doctorSign && (
                          <img
                            className="sign-image"
                            src={doctorSign}
                            alt="Doctor Signature"
                            style={{
                              display: "block",
                              maxWidth: 140,
                              height: "auto",
                              margin: "0 auto",
                              objectFit: "cover",
                              borderRadius: 9999,
                              background: "#ffffff",
                            }}
                            onError={(e) =>
                              (e.currentTarget.style.display = "")
                            }
                          />
                        )}

                        <div
                          style={{
                            marginTop: 8,
                            fontSize: 11,
                            color: "#374151",
                            textAlign: "center",
                          }}
                        >
                          {userDetails?.userSalutation}{" "}
                          {userDetails?.userFirstName}{" "}
                          {userDetails?.userLastName}
                          <div style={{ fontSize: 11 }}>
                            {userDetails?.qualification}
                          </div>
                          <div style={{ fontSize: 11 }}>
                            {userDetails?.speciality}
                          </div>
                          {userDetails?.registrationNumber ? (
                            <div style={{ fontSize: 11 }}>
                              Regn No. {userDetails.registrationNumber}
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div
                  className="footer-block"
                  style={{ flex: "0 0 auto", marginTop: 8 }}
                >
                  <FooterPrescription
                    clinicDetails={clinicDetails}
                    doctorData={userDetails}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* <div
            style={{
              position: "fixed",
              left: 0,
              right: 0,
              bottom: 12,
              display: "flex",
              justifyContent: "center",
              pointerEvents: "auto",
            }}
          >
            <button
              onClick={download}
              style={{
                background: "#2563eb",
                color: "#fff",
                padding: "8px 14px",
                borderRadius: 6,
              }}
            >
              Download Prescription
            </button>
          </div> */}
        </div>
      )}
    </>
  );
}
